import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Check, ChevronDown, FolderKanban, Loader2, Plus, RefreshCw, Save, Search, Trash2 } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components_dashboard/ui/alert-dialog';
import { Button } from '../components_dashboard/ui/button';
import { Input } from '../components_dashboard/ui/input';
import { Label } from '../components_dashboard/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../components_dashboard/ui/popover';
import { toast } from '../components/CustomToast';
import { useAuth } from '../context/AuthContext';

const apiUrl = process.env.REACT_APP_BACKEND_URL;
const NEW_GROUP_ID = '__new__';

const EMPTY_GROUP_DRAFT = {
  id: '',
  name: '',
  user_ids: [],
  __isNew: true,
};

export default function ClientGroupsDashboard() {
  const { token } = useAuth();
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [draft, setDraft] = useState(null);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [memberSearchQuery, setMemberSearchQuery] = useState('');

  const filteredGroups = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return groups;
    return groups.filter((entry) => String(entry.name || '').toLowerCase().includes(query));
  }, [groups, searchQuery]);

  const filteredUsers = useMemo(() => {
    const query = memberSearchQuery.trim().toLowerCase();
    if (!query) return users;
    return users.filter((entry) =>
      String(entry.name || '').toLowerCase().includes(query)
      || String(entry.email || '').toLowerCase().includes(query)
    );
  }, [users, memberSearchQuery]);

  const selectedGroup = useMemo(() => (
    groups.find((entry) => entry.id === selectedGroupId) || null
  ), [groups, selectedGroupId]);

  const userById = useMemo(() => (
    Object.fromEntries(users.map((entry) => [entry.id, entry]))
  ), [users]);

  const isCreatingGroup = Boolean(draft?.__isNew);

  const selectedMembers = useMemo(() => (
    (draft?.user_ids || [])
      .map((userId) => userById[userId])
      .filter(Boolean)
  ), [draft, userById]);

  const fetchGroups = useCallback(async () => {
    if (!token) return;
    try {
      setLoadingGroups(true);
      const response = await axios.get(`${apiUrl}/api/v1/admin/client-groups`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const rows = Array.isArray(response.data?.client_groups) ? response.data.client_groups : [];
      setGroups(rows);
      setSelectedGroupId((current) => {
        if (current === NEW_GROUP_ID) return current;
        if (current && rows.some((entry) => entry.id === current)) return current;
        return rows[0]?.id || '';
      });
    } catch (error) {
      console.error('Failed to fetch client groups', error);
      toast.error(error.response?.data?.detail || 'Failed to load client groups.');
      setGroups([]);
      setSelectedGroupId('');
    } finally {
      setLoadingGroups(false);
    }
  }, [token]);

  const fetchUsers = useCallback(async () => {
    if (!token) return;
    try {
      setLoadingUsers(true);
      const response = await axios.get(`${apiUrl}/api/v1/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(Array.isArray(response.data?.users) ? response.data.users : []);
    } catch (error) {
      console.error('Failed to fetch users', error);
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  }, [token]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    if (selectedGroupId === NEW_GROUP_ID) {
      setDraft({ ...EMPTY_GROUP_DRAFT });
    } else if (selectedGroup) {
      setDraft({ ...selectedGroup, __isNew: false });
    } else {
      setDraft(null);
    }
  }, [selectedGroup, selectedGroupId]);

  const handleCreateDraft = () => {
    setSelectedGroupId(NEW_GROUP_ID);
  };

  const handleFieldChange = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value }));
  };

  const handleMemberToggle = (userId, checked) => {
    setDraft((current) => {
      const existing = new Set(current?.user_ids || []);
      if (checked) {
        existing.add(userId);
      } else {
        existing.delete(userId);
      }
      return { ...current, user_ids: Array.from(existing) };
    });
  };

  const applySavedGroup = (saved, isNewRecord) => {
    setDraft({ ...saved, __isNew: false });
    setSelectedGroupId(saved.id);
    setGroups((current) => (
      isNewRecord
        ? [saved, ...current]
        : current.map((entry) => (entry.id === saved.id ? saved : entry))
    ));
  };

  const handleSave = async () => {
    if (!draft) return;

    if (!String(draft.name || '').trim()) {
      toast.error('Name is required.');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        name: draft.name || null,
        user_ids: draft.user_ids || [],
      };

      const response = isCreatingGroup
        ? await axios.post(`${apiUrl}/api/v1/admin/client-groups`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        })
        : await axios.patch(`${apiUrl}/api/v1/admin/client-groups/${selectedGroupId}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });

      const updatedGroup = response.data;
      applySavedGroup(updatedGroup, isCreatingGroup);
      toast.success(isCreatingGroup ? 'Client group created.' : 'Client group updated.');
    } catch (error) {
      console.error('Failed to save client group', error);
      toast.error(error.response?.data?.detail || 'Failed to save client group.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedGroupId || selectedGroupId === NEW_GROUP_ID) return;
    const deletedId = selectedGroupId;
    try {
      setDeleting(true);
      await axios.delete(`${apiUrl}/api/v1/admin/client-groups/${deletedId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const remaining = groups.filter((entry) => entry.id !== deletedId);
      setGroups(remaining);
      setSelectedGroupId(remaining[0]?.id || '');
      setConfirmDeleteOpen(false);
      toast.success('Client group deleted.');
    } catch (error) {
      console.error('Failed to delete client group', error);
      toast.error(error.response?.data?.detail || 'Failed to delete client group.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50 font-sans">
      <main className="flex-1 flex flex-col min-w-0 p-6 lg:p-8">
        <div className="mx-auto max-w-7xl space-y-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-violet-100 bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700">
                <FolderKanban className="w-3.5 h-3.5" />
                Client Groups
              </div>
              <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-900">Client Group Manager</h1>
              <p className="mt-1 text-slate-500">
                Name a bucket of accounts, then attach a DID number to the whole bucket from the DID Numbers page.
              </p>
            </div>

            <Button
              variant="outline"
              onClick={() => { fetchGroups(); fetchUsers(); }}
              disabled={loadingGroups}
              className="border-slate-200 text-slate-700 hover:bg-slate-100"
            >
              {loadingGroups ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Refresh
            </Button>
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-[340px_minmax(0,1fr)]">
            <div className="bg-white border rounded-2xl border-slate-200 shadow-sm">
              <div className="p-4 border-b border-slate-100">
                <div className="space-y-3">
                  <Button
                    onClick={handleCreateDraft}
                    className="w-full text-white bg-gradient-to-r from-violet-600 to-purple-700 hover:from-violet-700 hover:to-purple-800"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Client Group
                  </Button>
                  <div className="relative">
                    <Search className="absolute w-4 h-4 -translate-y-1/2 pointer-events-none left-3 top-1/2 text-slate-400" />
                    <Input
                      value={searchQuery}
                      onChange={(event) => setSearchQuery(event.target.value)}
                      placeholder="Search client groups..."
                      className="pl-9"
                    />
                  </div>
                </div>
              </div>

              <div className="max-h-[72vh] overflow-y-auto">
                {loadingGroups ? (
                  <div className="flex items-center justify-center gap-2 px-4 py-10 text-sm text-slate-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading client groups...
                  </div>
                ) : filteredGroups.length === 0 ? (
                  <div className="px-4 py-10 text-sm text-center text-slate-500">
                    No client groups match the current search.
                  </div>
                ) : (
                  filteredGroups.map((entry) => (
                    <button
                      key={entry.id}
                      type="button"
                      onClick={() => setSelectedGroupId(entry.id)}
                      className={`flex w-full items-start gap-3 border-b border-slate-100 px-4 py-4 text-left transition-colors last:border-b-0 ${
                        selectedGroupId === entry.id ? 'bg-violet-50' : 'hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center justify-center w-10 h-10 shrink-0 rounded-full bg-violet-100 text-violet-700">
                        <FolderKanban className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate text-slate-900">{entry.name || 'Unnamed group'}</div>
                        <div className="mt-1 text-sm text-slate-500">
                          {(entry.user_ids || []).length} account{(entry.user_ids || []).length === 1 ? '' : 's'}
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>

            <div className="bg-white border rounded-2xl border-slate-200 shadow-sm">
              {loadingGroups ? (
                <div className="flex min-h-[520px] items-center justify-center gap-2 text-sm text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading client group details...
                </div>
              ) : !draft ? (
                <div className="flex min-h-[520px] items-center justify-center gap-2 text-sm text-slate-500">
                  Select a client group to inspect, or create a new one.
                </div>
              ) : (
                <div className="p-6 space-y-6">
                  <div className="flex flex-col gap-4 pb-6 border-b border-slate-100 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <h2 className="text-xl font-semibold text-slate-900">{draft.name || 'Create Client Group'}</h2>
                      <p className="mt-1 text-sm text-slate-500">
                        {isCreatingGroup ? 'Name the group and add accounts, then save.' : `ID: ${draft.id}`}
                      </p>
                    </div>

                    <div className="flex gap-2">
                      {!isCreatingGroup && (
                        <Button
                          variant="outline"
                          onClick={() => setConfirmDeleteOpen(true)}
                          className="text-red-600 border-red-200 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </Button>
                      )}
                      <Button
                        onClick={handleSave}
                        disabled={saving}
                        className="text-white bg-gradient-to-r from-violet-600 to-purple-700 hover:from-violet-700 hover:to-purple-800"
                      >
                        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                        {isCreatingGroup ? 'Create Group' : 'Save Changes'}
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Name</Label>
                    <Input
                      value={draft.name || ''}
                      onChange={(event) => handleFieldChange('name', event.target.value)}
                      placeholder="e.g. Acme Corp"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Accounts in this group</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="flex min-h-11 w-full items-center justify-between gap-3 rounded-xl border border-violet-200 bg-white px-3 py-2 text-left text-sm shadow-sm transition hover:border-violet-300"
                        >
                          <div className="flex min-w-0 flex-1 flex-wrap gap-1.5">
                            {selectedMembers.length ? (
                              selectedMembers.map((member) => (
                                <span
                                  key={member.id}
                                  className="inline-flex max-w-full items-center gap-1 rounded-md bg-violet-50 px-2 py-1 text-xs font-medium text-slate-700"
                                >
                                  <span className="truncate">{member.name || member.email}</span>
                                </span>
                              ))
                            ) : (
                              <span className="text-slate-400">Select accounts for this group</span>
                            )}
                          </div>
                          <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent align="start" className="w-[420px] max-w-[calc(100vw-3rem)] p-2">
                        <div className="relative mb-2">
                          <Search className="absolute w-4 h-4 -translate-y-1/2 pointer-events-none left-3 top-1/2 text-slate-400" />
                          <Input
                            value={memberSearchQuery}
                            onChange={(event) => setMemberSearchQuery(event.target.value)}
                            placeholder="Search accounts by name or email..."
                            className="pl-9"
                            autoFocus
                          />
                        </div>
                        <div className="max-h-72 space-y-1 overflow-y-auto">
                          {loadingUsers ? (
                            <div className="flex items-center justify-center gap-2 px-2 py-6 text-sm text-slate-500">
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Loading accounts...
                            </div>
                          ) : filteredUsers.length === 0 ? (
                            <div className="px-2 py-6 text-sm text-center text-slate-500">
                              {users.length === 0 ? 'No accounts found.' : 'No accounts match your search.'}
                            </div>
                          ) : (
                            filteredUsers.map((user) => {
                              const isChecked = (draft.user_ids || []).includes(user.id);
                              return (
                                <div
                                  key={user.id}
                                  role="button"
                                  tabIndex={0}
                                  className="flex cursor-pointer items-start gap-3 rounded-lg px-2 py-2 hover:bg-violet-50"
                                  onClick={() => handleMemberToggle(user.id, !isChecked)}
                                  onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                      event.preventDefault();
                                      handleMemberToggle(user.id, !isChecked);
                                    }
                                  }}
                                >
                                  <span
                                    className="mt-1 flex shrink-0 items-center justify-center"
                                    style={{
                                      width: '16px',
                                      height: '16px',
                                      borderRadius: '4px',
                                      border: isChecked ? '2px solid #7c3aed' : '2px solid #000000',
                                      backgroundColor: isChecked ? '#7c3aed' : '#ffffff',
                                    }}
                                  >
                                    {isChecked && <Check className="h-3 w-3" style={{ color: '#ffffff' }} />}
                                  </span>
                                  <div className="min-w-0 flex-1">
                                    <span className="font-medium break-all text-slate-900">{user.name || 'Unnamed user'}</span>
                                    <p className="mt-0.5 text-xs break-all text-slate-500">{user.email}</p>
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>
                      </PopoverContent>
                    </Popover>
                    <p className="text-xs text-slate-500">
                      Any DID number attached to this group (on the DID Numbers page) is automatically granted to
                      every account listed here — and to anyone added to the group later.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <AlertDialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete client group?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the group and revokes any DID numbers it granted to its members. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700">
              {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
